import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import aboutUsBg from '../assets/About/istockphoto-1487950796-612x612.jpg';
import aboutUsImage from '../assets/Images/82.jpeg';
import chairmanImage from '../assets/Images/Untitled design.png';
import teamImage from '../assets/Images/128.jpeg';
import servicesImage from '../assets/Images/129.jpeg';
import aboutContactImage from '../assets/About/istockphoto-1466744915-612x612.jpg';

// Animation variants (unchanged)
const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const scaleUp = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: 'easeOut' } },
};

const AboutUs = () => {
    const { t } = useTranslation('aboutus');

    const servicesList = [
        t('services.crafting'),
        t('services.sourcing'),
        t('services.consultations'),
        t('services.repair'),
    ];

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Hero Section */}
            <div className="relative flex h-[60vh] w-full items-center justify-center font-lato md:h-[50vh] lg:h-[80vh]">
                <img
                    src={aboutUsBg}
                    alt={t('hero.alt')}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#020202c7] to-[#42424259]" />

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={textVariants}
                    className="container relative z-10 flex items-center justify-center pt-[100px]"
                >
                    <div className="w-full">
                        <h1 className="text-center text-[50px] font-semibold leading-[80px] text-white font-copperplate md:text-[60px] lg:text-[80px]">
                            {t('hero.title')}
                        </h1>
                        <div className="flex justify-center text-center leading-8">
                            <p className="mt-6 max-w-[90%] font-lato text-[16px] font-semibold tracking-[5%] text-white sm:mt-10 sm:max-w-2xl sm:text-[25px]">
                                {t('hero.subtitle.part1')}{' '}
                                <span className="font-lato text-[16px] font-semibold tracking-[5%] text-[#BF943A] sm:text-[25px]">
                                    {t('hero.subtitle.highlight')}
                                </span>{' '}
                                {t('hero.subtitle.part2')}
                            </p>
                        </div>

                        <div className="absolute left-[30px] top-10">
                            <svg width="289" height="245" viewBox="0 0 289 245" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M289 1H1V244.5" stroke="white" />
                            </svg>
                        </div>
                        <div className="absolute -bottom-10 right-[30px]">
                            <svg width="289" height="244" viewBox="0 0 289 244" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0 243.5H288V0" stroke="white" />
                            </svg>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* About Us Content Section */}
            <section className="bg-[#1F1F1F] py-12">
                <div className="container font-lato text-white">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="flex items-center gap-5">
                        <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                        <h1 className="text-[25px] font-semibold uppercase text-white md:text-[30px] lg:text-[40px]">
                            {t('whoWeAre.title')}
                        </h1>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="mt-2 flex justify-between">
                        <div className="w-full text-[12px] font-semibold text-[#AEAEAE] md:text-[16px] lg:w-[60%] lg:text-[18px]">
                            {t('whoWeAre.subtitle')}
                        </div>
                    </motion.div>

                    <div className="my-6 h-px w-full bg-[#D7D7D7] lg:my-12"></div>

                    <div className="justify-between gap-10 lg:flex">
                        <div className="relative w-full space-y-4 lg:w-[45%]">
                            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={scaleUp} className="relative z-10 h-[330px] lg:h-[450px]">
                                <img src={aboutUsImage} alt={t('whoWeAre.imageAlt')} className="h-full w-full rounded-lg object-cover" />
                            </motion.div>
                            {/* decorative svgs unchanged */}
                        </div>

                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="relative my-10 block w-full lg:hidden lg:w-[50%]">
                            <p className="leading-8 text-[14px] font-semibold text-white md:text-[18px] lg:text-[20px]">
                                {t('whoWeAre.text.part1')}{' '}
                                <span className="text-[28px] text-[#BF943A] md:text-[32px] lg:text-[35px]">{t('brandName')}</span>{' '}
                                {t('whoWeAre.text.part2')}{' '}
                                <span className="text-[#BF943A]">{t('whoWeAre.text.pieces')}</span>{' '}
                                {t('whoWeAre.text.part3')}{' '}
                                <span className="text-[#BF943A]">{t('whoWeAre.text.regions')}</span>{' '}
                                {t('whoWeAre.text.part4')}
                            </p>
                        </motion.div>

                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="relative hidden h-[550px] w-full lg:block lg:w-[45%]">
                            <p className="leading-8 text-[16px] font-semibold text-white md:text-[18px] lg:text-[20px]">
                                {t('whoWeAre.text.part1')}{' '}
                                <span className="text-[28px] text-[#BF943A] md:text-[32px] lg:text-[35px]">{t('brandName')}</span>{' '}
                                {t('whoWeAre.text.part2')}{' '}
                                <span className="text-[#BF943A]">{t('whoWeAre.text.pieces')}</span>{' '}
                                {t('whoWeAre.text.part3')}{' '}
                                <span className="text-[#BF943A]">{t('whoWeAre.text.regions')}</span>{' '}
                                {t('whoWeAre.text.part4')}
                            </p>
                        </motion.div>
                    </div>

                    {/* Chairman's Message Section */}
                    <div className="">
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="flex items-center gap-5">
                            <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                            <h1 className="text-[25px] font-semibold uppercase text-white md:text-[30px] lg:text-[40px]">
                                {t('chairman.title')}
                            </h1>
                        </motion.div>

                        <div className="my-6 h-px w-full bg-[#D7D7D7] lg:my-12"></div>

                        {/* SM Layout: Vertical */}
                        <div className="block sm:hidden">
                            <div className="space-y-6">
                                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={scaleUp} className="relative z-10 h-[330px]">
                                    <img src={chairmanImage} alt={t('chairman.alt')} className="h-full w-full rounded-lg object-cover" />
                                </motion.div>

                                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants}>
                                    <p className="leading-8 text-[14px] font-semibold text-white">
                                        {t('chairman.message')}
                                    </p>
                                    <p className="mt-4 text-[14px] font-semibold text-[#BF943A]">
                                        {t('chairman.signature')}
                                    </p>
                                </motion.div>
                            </div>
                        </div>

                        {/* MD+ Layout: Side-by-side */}
                        <div className="hidden sm:flex justify-between gap-10">
                            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="relative my-10 w-full lg:w-[45%]">
                                <p className="leading-8 text-[14px] font-semibold text-white md:text-[18px] lg:text-[20px]">
                                    {t('chairman.message')}
                                </p>
                                <p className="mt-4 text-[14px] font-semibold text-[#BF943A] md:text-[16px] lg:text-[18px]">
                                    {t('chairman.signature')}
                                </p>
                            </motion.div>

                            <div className="relative w-full space-y-4 lg:w-[45%]">
                                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={scaleUp} className="relative z-10 h-[630px] lg:h-[650px]">
                                    <img src={chairmanImage} alt={t('chairman.alt')} className="h-full w-full rounded-lg object-cover" />
                                </motion.div>
                                {/* decorative svgs */}
                            </div>
                        </div>
                    </div>

                    {/* Our Team Section */}
                    <div className="mt-12 lg:mt-20">
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="flex items-center gap-5">
                            <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                            <h1 className="text-[25px] font-semibold uppercase text-white md:text-[30px] lg:text-[40px]">
                                {t('team.title')}
                            </h1>
                        </motion.div>

                        <div className="my-6 h-px w-full bg-[#D7D7D7] lg:my-12"></div>

                        <div className="justify-between gap-10 lg:flex">
                            <div className="relative w-full space-y-4 lg:w-[45%]">
                                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={scaleUp} className="relative z-10 h-[330px] lg:h-[450px]">
                                    <img src={teamImage} alt={t('team.alt')} className="h-full w-full rounded-lg object-cover" />
                                </motion.div>
                                {/* decorative svgs */}
                            </div>

                            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="relative my-10 block w-full lg:hidden lg:w-[50%]">
                                <p className="leading-8 text-[14px] font-semibold text-white md:text-[18px] lg:text-[20px]">
                                    {t('team.text.part1')}{' '}
                                    <span className="text-[28px] text-[#BF943A] md:text-[32px] lg:text-[35px]">{t('brandName')}</span>{' '}
                                    {t('team.text.part2')}{' '}
                                    <span className="text-[#BF943A]">{t('team.text.highlight')}</span>{' '}
                                    {t('team.text.part3')}{' '}
                                    <span className="text-[#BF943A]">{t('team.text.regions')}</span>{' '}
                                    {t('team.text.part4')}
                                </p>
                            </motion.div>

                            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="relative hidden h-[550px] w-full lg:block lg:w-[45%]">
                                <p className="leading-8 text-[16px] font-semibold text-white md:text-[18px] lg:text-[20px]">
                                    {t('team.text.part1')}{' '}
                                    <span className="text-[28px] text-[#BF943A] md:text-[32px] lg:text-[35px]">{t('brandName')}</span>{' '}
                                    {t('team.text.part2')}{' '}
                                    <span className="text-[#BF943A]">{t('team.text.highlight')}</span>{' '}
                                    {t('team.text.part3')}{' '}
                                    <span className="text-[#BF943A]">{t('team.text.regions')}</span>{' '}
                                    {t('team.text.part4')}
                                </p>
                            </motion.div>
                        </div>
                    </div>

                    {/* Our Services Section */}
                    <div className="">
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="flex items-center gap-5">
                            <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                            <h1 className="text-[25px] font-semibold uppercase text-white md:text-[30px] lg:text-[40px]">
                                {t('services.title')}
                            </h1>
                        </motion.div>

                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="mt-2 flex justify-between">
                            <div className="w-full text-[12px] font-semibold text-[#AEAEAE] md:text-[16px] lg:w-[60%] lg:text-[18px]">
                                {t('services.subtitle')}
                            </div>
                        </motion.div>

                        <div className="my-6 h-px w-full bg-[#D7D7D7] lg:my-12"></div>

                        <div className="gap-5 lg:flex">
                            <div className="w-full lg:w-[40%]">
                                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={scaleUp} className="relative z-10 h-[300px] w-[100%] overflow-hidden rounded-lg md:h-[450px] lg:h-full">
                                    <img src={servicesImage} alt={t('services.alt')} className="h-full w-full object-cover" />
                                </motion.div>
                            </div>

                            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="mt-8 w-full lg:mt-0 lg:w-[60%]">
                                <p className="w-full text-[16px] md:text-[18px] lg:w-[70%] lg:text-[25px]">
                                    {t('services.intro')}
                                </p>
                                <div className="mt-5">
                                    <ul className="list-inside list-[circle] space-y-5">
                                        {servicesList.map((service, index) => (
                                            <li key={index} className="border-b border-[#535353] pb-5 text-[16px] font-semibold text-[#C2C2C2] first-letter:capitalize md:text-[18px] lg:text-[22px]">
                                                {service}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </motion.div>
                        </div>
                    </div>

                    {/* About Contact Section */}
                    <div className="relative w-full h-[60vh] md:h-[40vh] lg:h-[70vh] mt-16 bg-cover bg-center flex items-center justify-center">
                        <img src={aboutContactImage} alt={t('contact.alt')} className="absolute inset-0 h-full w-full object-cover" />
                        <div className="absolute inset-0 bg-black/50" />
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="relative z-10 flex flex-col items-center justify-center gap-4 sm:gap-6 px-4 sm:px-6 text-center">
                            <h1 className="font-lato font-bold text-[20px] sm:text-3xl lg:text-5xl text-white leading-tight">
                                {t('contact.title')}
                            </h1>
                            <p className="font-lato font-medium text-[14px] sm:text-base lg:text-xl text-white max-w-[90%] sm:max-w-3xl leading-relaxed">
                                {t('contact.text')}
                            </p>
                            <a href="/contact-us" className="inline-block">
                                <button className="px-6 sm:px-8 py-2 sm:py-3 bg-[#BF943A] rounded-lg font-lato font-bold text-base sm:text-lg lg:text-xl text-[#0C0C0C] transition-all duration-300">
                                    {t('contact.button')}
                                </button>
                            </a>
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AboutUs;